/**
 * Pure helpers for the printer duplication name-uniqueness check.
 *
 * Extracted so the rules (per-shop scope, oneShop flat shape vs multi-shop
 * nested shape, archived exclusion, case-insensitive normalization) can be
 * unit-tested without rendering the modal.
 */

export function normalize(value: string): string {
    return value.trim().toLowerCase();
}

type PrinterRow = {
    isArchived?: boolean;
    designation?: unknown;
    [key: string]: unknown;
};

type ShopRow = {
    shopId?: string;
    subRows?: PrinterRow[];
};

export type DataTableLike = {
    data?: Array<PrinterRow | ShopRow>;
};

function extractName(
    printer: PrinterRow,
    printerNameKey: string
): string | null {
    const candidates: unknown[] = [
        printer?.[printerNameKey],
        printer?.designation,
    ];
    for (const candidate of candidates) {
        if (typeof candidate === "string" && candidate.trim().length > 0) {
            return candidate;
        }
    }
    return null;
}

/**
 * Build the set of normalized designations of non-archived printers within
 * the same shop as the row being duplicated. Used to flag a user-entered
 * designation as already in use.
 *
 * Archived printers are intentionally excluded: archiving frees the name
 * for reuse. The check is case-insensitive (see `normalize`).
 *
 * dataTable shape:
 *  - `oneShop=true`: `dataTable.data` is a flat list of printer rows.
 *  - `oneShop=false`: `dataTable.data` is grouped by shop; each shop row
 *    has `shopId` and `subRows: PrinterRow[]`.
 */
export function buildExistingDesignations(
    dataTable: DataTableLike | null | undefined,
    oneShop: boolean,
    shopId: string,
    printerNameKey: string
): Set<string> {
    const rows = dataTable?.data ?? [];
    const printers: PrinterRow[] = oneShop
        ? (rows as PrinterRow[])
        : (rows as ShopRow[])
              .filter((shop) => shop.shopId === shopId)
              .flatMap((shop) => shop.subRows ?? []);

    const set = new Set<string>();
    for (const printer of printers) {
        if (printer?.isArchived === true) continue;
        const name = extractName(printer, printerNameKey);
        if (name !== null) set.add(normalize(name));
    }
    return set;
}

export function isDuplicateDesignation(
    designation: string,
    existingDesignations: Set<string>
): boolean {
    const trimmed = designation.trim();
    if (trimmed === "") return false;
    return existingDesignations.has(normalize(designation));
}
